import { CheckCircle, ClockCountdown, Info, WarningCircle } from '@phosphor-icons/react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { usePricingContext } from '@/components/pricing/PricingContext'
import { useDistributionBenefitProgress } from '@/components/pricing/useDistributionBenefitProgress'
import { cn } from '@/lib/utils'
import { Trans, useTranslation } from 'react-i18next'

type DistributionPriceLearnMoreProps = {
  label?: string
  triggerClassName?: string
}

type StatusTone = 'success' | 'warning' | 'danger' | 'neutral'

const STATUS_CHIP_CLASS: Record<StatusTone, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  warning: 'border-amber-200 bg-amber-50 text-amber-700',
  danger: 'border-rose-200 bg-rose-50 text-rose-700',
  neutral: 'border-slate-200 bg-slate-100 text-slate-700',
}

const STATUS_PANEL_CLASS: Record<StatusTone, string> = {
  success: 'border-emerald-200/80',
  warning: 'border-amber-200/80',
  danger: 'border-rose-200/80',
  neutral: 'border-zinc-200',
}

const STATUS_ICON_CLASS: Record<StatusTone, string> = {
  success: 'bg-emerald-100 text-emerald-700',
  warning: 'bg-amber-100 text-amber-700',
  danger: 'bg-rose-100 text-rose-700',
  neutral: 'bg-slate-100 text-slate-700',
}

const buildMetricCard = (label: string, value: string) => (
  <div key={label} className="rounded-[14px] border border-zinc-200 bg-white px-4 py-3">
    <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
      {label}
    </dt>
    <dd className="mt-1 text-sm font-semibold text-zinc-950">{value}</dd>
  </div>
)

export function DistributionPriceLearnMore({
  label,
  triggerClassName,
}: DistributionPriceLearnMoreProps) {
  const pricingContext = usePricingContext()
  const { t } = useTranslation()
  const {
    resolvedInfo,
    requiredPurchaseAmount,
    requiredPurchaseLabel,
    progressCurrentLabel,
    progressTargetLabel,
    progressPercent,
    progressPercentLabel,
    remainingDaysLabel,
    hasRemainingDays,
    canShowDetailedExplainer,
  } = useDistributionBenefitProgress()

  const descriptionContent = canShowDetailedExplainer
    ? (
      <Trans
        i18nKey="product.distribution_modal_description_detailed"
        values={{
          balance: requiredPurchaseLabel,
          days: remainingDaysLabel,
        }}
        components={{
          strong: <strong className="font-semibold text-zinc-950" />,
        }}
      />
    )
    : t('product.distribution_modal_description')

  const metrics = [
    {
      label: t('product.distribution_modal_card_period_balance'),
      value: requiredPurchaseLabel,
    },
    {
      label: t('product.distribution_modal_card_deadline'),
      value: remainingDaysLabel,
    },
  ]

  const statusDescription = (() => {
    if (!resolvedInfo) {
      return t('product.distribution_modal_status_unavailable')
    }

    if (resolvedInfo.mode === 'active') {
      return t('product.distribution_modal_status_active')
    }

    if (requiredPurchaseAmount !== null && requiredPurchaseAmount <= 0) {
      return t('product.distribution_modal_status_grace_goal_met')
    }

    if (resolvedInfo.mode === 'grace' && hasRemainingDays) {
      return t('product.distribution_modal_status_grace', {
        balance: requiredPurchaseLabel,
        days: remainingDaysLabel,
      })
    }

    if (resolvedInfo.mode === 'grace') {
      return t('product.distribution_modal_status_pending_no_deadline', {
        balance: requiredPurchaseLabel,
      })
    }

    return t('product.distribution_modal_status_inactive', {
      balance: requiredPurchaseLabel,
    })
  })()

  const statusMeta = (() => {
    if (!pricingContext.distributionEnabled) {
      return {
        tone: 'neutral' as const,
        label: t('account.benefits.distribution_status_unavailable'),
        title: t('header.account.notifications.distribution_title'),
        icon: Info,
      }
    }

    if (!resolvedInfo) {
      return {
        tone: 'neutral' as const,
        label: t('account.benefits.distribution_status_unavailable'),
        title: t('header.account.notifications.distribution_unavailable_title'),
        icon: Info,
      }
    }

    if (resolvedInfo.mode === 'active') {
      return {
        tone: 'success' as const,
        label: t('account.benefits.distribution_current_status_active'),
        title: t('header.account.notifications.distribution_active_title'),
        icon: CheckCircle,
      }
    }

    if (resolvedInfo.mode === 'grace' && resolvedInfo.urgentRisk) {
      return {
        tone: 'danger' as const,
        label: t('account.benefits.distribution_current_status_urgent'),
        title: t('header.account.notifications.distribution_urgent_title'),
        icon: WarningCircle,
      }
    }

    if (resolvedInfo.mode === 'grace') {
      return {
        tone: 'warning' as const,
        label: t('account.benefits.distribution_current_status_grace'),
        title: t('header.account.notifications.distribution_risk_title'),
        icon: ClockCountdown,
      }
    }

    return {
      tone: 'danger' as const,
      label: t('account.benefits.distribution_current_status_inactive'),
      title: t('header.account.notifications.distribution_lost_title'),
      icon: WarningCircle,
    }
  })()

  const StatusIcon = statusMeta.icon

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="link"
          size="sm"
          className={cn(
            'h-auto px-0 text-xs font-semibold text-slate-900 underline-offset-4 hover:text-slate-700',
            triggerClassName,
          )}
        >
          {label || t('product.distribution_learn_more')}
        </Button>
      </DialogTrigger>

      <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] max-h-[calc(100dvh-1rem)] overflow-hidden rounded-[22px] border-zinc-200 bg-white p-0 shadow-[0_32px_80px_-48px_rgba(15,23,42,0.34)] sm:max-h-[calc(100dvh-3rem)] sm:max-w-[min(52rem,calc(100vw-2rem))] [&_[data-slot=dialog-close]]:top-4 [&_[data-slot=dialog-close]]:right-4 [&_[data-slot=dialog-close]]:rounded-full [&_[data-slot=dialog-close]]:border [&_[data-slot=dialog-close]]:border-zinc-200 [&_[data-slot=dialog-close]]:bg-white [&_[data-slot=dialog-close]]:p-1.5 [&_[data-slot=dialog-close]]:opacity-100 [&_[data-slot=dialog-close]]:shadow-[0_8px_18px_-14px_rgba(15,23,42,0.35)]">
        <div className="max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain sm:max-h-[calc(100dvh-3rem)]">
          <div className="border-b border-zinc-100 px-5 py-5 sm:px-6 sm:py-6 md:px-7">
            <DialogHeader className="space-y-2.5 text-left">
              <div className="inline-flex w-fit rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-700">
                {t('product.distribution_modal_badge')}
              </div>
              <DialogTitle className="pr-10 text-[1.25rem] leading-tight font-semibold tracking-tight text-zinc-950 sm:max-w-[40rem] sm:text-[1.4rem]">
                {t('product.distribution_modal_title')}
              </DialogTitle>
              <DialogDescription className="text-sm leading-6 text-zinc-600 sm:max-w-[44rem]">
                {descriptionContent}
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-4 px-5 py-5 sm:px-6 sm:py-6 md:px-7">
            <section className={cn(
              'rounded-[18px] border bg-zinc-50/70 p-4',
              STATUS_PANEL_CLASS[statusMeta.tone],
            )}>
              <div className="flex items-start gap-3">
                <div className={cn(
                  'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl',
                  STATUS_ICON_CLASS[statusMeta.tone],
                )}>
                  <StatusIcon className="h-5 w-5" weight="fill" />
                </div>

                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                      {t('product.distribution_modal_status_title')}
                    </p>

                    <span className={cn(
                      'inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold',
                      STATUS_CHIP_CLASS[statusMeta.tone],
                    )}>
                      {statusMeta.label}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    <p className="text-base font-semibold text-zinc-950">{statusMeta.title}</p>
                    <p className="text-sm leading-6 text-zinc-600">{statusDescription}</p>
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-[18px] border border-zinc-200 bg-white p-4">
              <p className="text-sm font-semibold text-zinc-950">
                {t('product.distribution_modal_numbers_title')}
              </p>
              {progressPercent !== null ? (
                <div className="mt-4 space-y-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                      {t('product.distribution_modal_progress_title')}
                    </p>
                    <p className="text-sm font-semibold text-zinc-950">
                      {progressPercentLabel}
                    </p>
                  </div>
                  <Progress
                    value={progressPercent}
                    className="h-2.5 bg-zinc-100 [&_[data-slot=progress-indicator]]:bg-amber-500"
                  />
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs leading-5 text-zinc-500">
                    <span>
                      {t('product.distribution_modal_progress_current', { value: progressCurrentLabel })}
                    </span>
                    <span className="font-medium text-zinc-600">
                      {t('product.distribution_modal_progress_target', { value: progressTargetLabel })}
                    </span>
                  </div>
                </div>
              ) : null}
              <dl className="mt-3 grid gap-3 sm:grid-cols-2">
                {metrics.map(({ label: metricLabel, value }) => buildMetricCard(metricLabel, value))}
              </dl>
            </section>

            <section className="rounded-[18px] border border-zinc-200 bg-white p-4">
              <p className="text-sm font-semibold text-zinc-950">
                {t('product.distribution_modal_overview_title')}
              </p>
              <div className="mt-3 space-y-2 text-sm leading-6 text-zinc-600">
                <p>{t('product.distribution_modal_overview_1')}</p>
                <p>{t('product.distribution_modal_overview_2')}</p>
                <p>{t('product.distribution_modal_overview_3')}</p>
              </div>
            </section>

          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
